/* When the intro stops and the site is revealed.
 *
 * Three forces pull against each other, and this module is where they are
 * reconciled:
 *
 * - The intro is a first impression, not a spinner, so it always plays for its
 *   full length even when everything is already loaded.
 * - The reveal should land on a dragon that is already drawing, so a nearly
 *   ready model is worth waiting a moment for.
 * - A visitor on a slow connection must never be held hostage. The 7.5MB model
 *   can take a minute on a throttled link, and no intro is worth a minute.
 *
 * So: play for MIN_INTRO_MS, then reveal as soon as the model is ready, and
 * give up waiting after GRACE_MS regardless. The dragon fades in on arrival
 * (see DragonParticles) rather than popping, which is what makes the bounded
 * wait acceptable instead of merely tolerable.
 *
 * The grace is only extended to a model that is actually about to arrive. Once
 * the 7.5MB download has landed, sampling takes a fraction of a second and
 * waiting for it is what buys the seamless reveal. A download still in flight
 * is a different matter: measured on a throttled link it has upwards of a
 * minute left to run, so holding the intro open for it would burn a second of
 * the visitor's time and change nothing.
 */

/** The intro animation's own length. Unchanged from the original 3.5s timer. */
export const MIN_INTRO_MS = 3500;

/** The longest the reveal will be held back waiting for the model. */
export const GRACE_MS = 1000;

/** The dragon is sampled and will draw on the next frame. */
export const ASSET_READY = "ready";
/** Downloaded; sampling is under way and will finish shortly. */
export const ASSET_SAMPLING = "sampling";
/** Still downloading, or not started. No telling how long it has left. */
export const ASSET_DOWNLOADING = "downloading";

/**
 * Decides what the preloader should do at one instant.
 *
 * @param {number} elapsedMs how long the intro has been playing
 * @param {string} assetState one of ASSET_READY, ASSET_SAMPLING, ASSET_DOWNLOADING
 * @returns {{reveal: boolean, waitMs: number, reason: string}}
 *   `reveal` is whether to finish now. When it is false, `waitMs` is how long
 *   until the decision could next change, so a caller can sleep exactly that
 *   long instead of polling.
 */
export function decideReveal(elapsedMs, assetState) {
  if (elapsedMs < MIN_INTRO_MS) {
    return {
      reveal: false,
      waitMs: MIN_INTRO_MS - elapsedMs,
      reason: "intro-playing",
    };
  }

  // The animation has run its course. From here the model decides.
  if (assetState === ASSET_READY) {
    return { reveal: true, waitMs: 0, reason: "asset-ready" };
  }

  const graceLeft = MIN_INTRO_MS + GRACE_MS - elapsedMs;
  if (assetState === ASSET_SAMPLING && graceLeft > 0) {
    return { reveal: false, waitMs: graceLeft, reason: "grace" };
  }

  // Either out of patience, or the download is nowhere near done. Show the site
  // and let the dragon fade in whenever it lands.
  return {
    reveal: true,
    waitMs: 0,
    reason: graceLeft > 0 ? "asset-far-off" : "grace-expired",
  };
}
